import { useState } from "react";
import {
  Chip,
  Divider,
  ListItemText,
  Menu,
  MenuItem,
  Typography,
  Box,
} from "@mui/material";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import { AiStudioRoute } from "hooks/useAiStudioChat";

/**
 * Header chip showing which specialist the Assistant is currently using,
 * with a menu to change it by hand.
 *
 * A manual change applies to the NEXT message: the backend records the turn
 * that is already streaming under the route it actually ran with, and
 * relabelling it here would make `chathistory.route` lie.
 *
 * Same popup pattern as ModelProviderMenu.
 */

export const ROUTE_LABELS: Record<AiStudioRoute, string> = {
  ai_studio: "I tuoi dati",
  guide: "Guida all'app",
  model: "Modellazione processo",
  robot: "Robot e form",
};

const ROUTE_DESCRIPTIONS: Record<AiStudioRoute, string> = {
  ai_studio: "Interrogo i tuoi processi, task e organizzazioni in tempo reale",
  guide: "Ti spiego passo passo come si fa dentro Flowenti",
  model: "Progettiamo insieme il diagramma BPMN del processo",
  robot: "Genero lo schema della form o il codice del robot",
};

const ROUTE_ORDER: AiStudioRoute[] = ["ai_studio", "guide", "model", "robot"];

interface RouteBadgeProps {
  route: AiStudioRoute | null;
  onChange: (route: AiStudioRoute) => void;
}

const RouteBadge: React.FC<RouteBadgeProps> = ({ route, onChange }) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  const handleSelect = (next: AiStudioRoute) => {
    setAnchorEl(null);
    if (next !== route) onChange(next);
  };

  return (
    <>
      <Chip
        size="small"
        icon={<AutoAwesomeIcon fontSize="small" />}
        label={route ? ROUTE_LABELS[route] : "Modalità automatica"}
        onClick={(e) => setAnchorEl(e.currentTarget)}
        variant={route ? "filled" : "outlined"}
        sx={{ cursor: "pointer" }}
      />
      <Menu
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        slotProps={{ paper: { sx: { minWidth: 300, borderRadius: 2, mt: 0.5, p: 1 } } }}
      >
        <Box sx={{ px: 1, pt: 0.5, pb: 1 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
            Modalità
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Di norma la scelgo io dalla tua domanda. Vale dal prossimo messaggio.
          </Typography>
        </Box>
        <Divider sx={{ my: 0.5 }} />
        {ROUTE_ORDER.map((value) => (
          <MenuItem
            key={value}
            selected={value === route}
            onClick={() => handleSelect(value)}
            sx={{ borderRadius: 1, mx: 0.5, whiteSpace: "normal" }}
          >
            <ListItemText
              primary={ROUTE_LABELS[value]}
              secondary={ROUTE_DESCRIPTIONS[value]}
              primaryTypographyProps={{ fontWeight: 500 }}
            />
          </MenuItem>
        ))}
      </Menu>
    </>
  );
};

export default RouteBadge;
